import React, { FunctionComponent, RefObject, useCallback } from 'react';

import { Copy } from '@yandex-360/iconostasis/dist/icons/Copy';
import { ButtonClose } from '@yandex-360/orb-components/dist/components/ButtonClose';
import { Divider } from '@yandex-360/orb-components/dist/components/Divider';
import { Popover } from '@yandex-360/orb-components/dist/components/Popover2';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import { useSelector } from 'react-redux';

import { i18n } from '../../../../../../shared/helpers/i18n';
import { InviteOrCopyFooter } from '../../../../components/invite-or-copy-footer';
import { useShareCallData } from '../../../../hooks/share-call-data.hook';
import {
    getConferenceShareLink,
    selectConferencePhones,
    selectIsConferencePhoneAvail,
    selectMeetingName,
} from '../../../../selectors';
import css from './styles.css';

type Props = {
    visible: boolean;
    anchor: RefObject<HTMLElement>;
    onClose: () => void;
};

const CopyLinkPopup: FunctionComponent<Props> = ({ visible, anchor, onClose }) => {
    const { callNumber } = useShareCallData();
    const meetingName = useSelector(selectMeetingName);

    const conferenceShareLinkWithoutProtocol = useSelector((state) =>
        getConferenceShareLink(state)?.replace(/^https:\/\//, '')
    );

    const conferencePhones = useSelector(selectConferencePhones);
    const isConferencePhoneAvail = useSelector(selectIsConferencePhoneAvail);

    const { copyConferenceUrlToClipboard, copyPhoneToClipboard } = useShareCallData();

    const content = (
        <>
            <Text
                className={css.callInfo}
                typography="body-md-medium"
                maxLines={3}
                overflow="ellipsis"
                color="primary"
                testId="formatted-call-info"
                hyphens="auto"
            >
                {meetingName ?? i18n('call', 'share-link-meeting-number', { number: callNumber })}
            </Text>
            {isConferencePhoneAvail && (
                <div className={css.copyLinkOrPhone}>
                    <div className={css.row}>
                        <Text className={css.link} typography="body-md-regular" overflow="ellipsis" maxLines={1}>
                            {conferenceShareLinkWithoutProtocol}
                        </Text>
                        <Copy
                            className={css.copyIcon}
                            variant="solid"
                            size="sm"
                            onClick={copyConferenceUrlToClipboard}
                        />
                    </div>
                    {conferencePhones?.map((conferencePhone) => (
                        <>
                            <Divider />
                            <div className={css.row}>
                                <div>
                                    <div>
                                        <Text typography="body-md-regular">{conferencePhone.number}</Text>
                                    </div>
                                    <div>
                                        <Text typography="caption-md-regular" className={css.additionalNumber}>
                                            {i18n('call', 'additional-number-template', {
                                                number: conferencePhone.extension,
                                            })}
                                        </Text>
                                    </div>
                                </div>
                                <Copy
                                    className={css.copyIcon}
                                    variant="solid"
                                    size="sm"
                                    onClick={() => copyPhoneToClipboard(conferencePhone)}
                                />
                            </div>
                        </>
                    ))}
                </div>
            )}
            <ButtonClose className={css.close} onClick={onClose} />

            <InviteOrCopyFooter className={css.inviteOrCopyFooter} onAddUsers={onClose} overlay />
        </>
    );

    const onOpenChange = useCallback(
        (isOpen) => {
            if (!isOpen) {
                // этот костыль нужен из-за несовсместимости кнопки из Lego и Popover2 из орбиты
                // todo: когда в тулбаре будут кнопки из орбиты, то надо будет удалить setTimeout
                setTimeout(onClose, 0);
            }
        },
        [onClose]
    );

    return (
        <Popover
            className={css.popover}
            isOpen={visible}
            triggerRef={anchor}
            onOpenChange={onOpenChange}
            style={{ left: '-100%' }}
            preventInteractOutside={false}
        >
            {content}
        </Popover>
    );
};

export { CopyLinkPopup };
